import React, { useState } from "react";
import { useNode } from "@craftjs/core";
import "../../styles/components/user/UserComponents.css";
import "../../styles/landing.css";

type ButtonVariant = "primary" | "secondary" | "outlined" | "text";
type ButtonSize = "small" | "medium" | "large";
type ButtonWidth = "auto" | "full";

interface ButtonProps {
  text?: string;
  backgroundColor?: string;
  color?: string;
  fontSize?: number;
  borderRadius?: number;
  padding?: string;
  margin?: string;
  url?: string;
  variant?: ButtonVariant;
  size?: ButtonSize;
  width?: ButtonWidth;
  icon?: string;
  iconPosition?: "left" | "right";
  hoverEffect?: boolean;
  hoverColor?: string;
  hoverTextColor?: string;
  disabled?: boolean;
  borderColor?: string;
  onClick?: () => void;
  fontWeight?: string; // Add fontWeight property
}

export const Button = ({
  text = "Click Me",
  backgroundColor = "#007bff",
  color = "#ffffff",
  fontSize = 16,
  borderRadius = 4,
  padding = "12px 24px",
  margin = "0",
  url = "",
  variant = "primary",
  size = "medium",
  width = "auto",
  icon = "",
  iconPosition = "left",
  hoverEffect = true,
  hoverColor = "",
  hoverTextColor = "",
  disabled = false,
  borderColor = "",
  onClick,
  fontWeight = "normal", // Add fontWeight with default value
}: ButtonProps) => {
  const [isHovering, setIsHovering] = useState(false);

  const {
    connectors: { connect, drag },
    selected,
    actions,
  } = useNode((node) => ({
    selected: node.events.selected,
  }));

  // Calculate styles based on variant, size, etc.
  const getButtonStyles = () => {
    let styles: React.CSSProperties = {
      backgroundColor,
      color,
      fontSize: `${fontSize}px`,
      fontWeight, // Apply fontWeight to button styles
      borderRadius: `${borderRadius}px`,
      padding,
      opacity: disabled ? 0.6 : 1,
      cursor: disabled ? "not-allowed" : "pointer",
      width: width === "full" ? "100%" : "auto",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      transition: "all 0.3s ease",
      border: "none",
    };

    // Style based on variant
    switch (variant) {
      case "outlined":
        styles = {
          ...styles,
          backgroundColor: "transparent",
          border: `1px solid ${borderColor || backgroundColor}`,
          color: borderColor || backgroundColor,
        };
        break;
      case "text":
        styles = {
          ...styles,
          backgroundColor: "transparent",
          border: "none",
          color: backgroundColor,
          padding: "8px 16px",
        };
        break;
      case "secondary":
        styles = {
          ...styles,
          backgroundColor: "#6c757d",
          color: "#ffffff",
        };
        break;
    }

    // Apply size specific styles
    switch (size) {
      case "small":
        styles = {
          ...styles,
          padding: variant === "text" ? "4px 8px" : "8px 16px",
          fontSize: `${fontSize * 0.85}px`,
        };
        break;
      case "large":
        styles = {
          ...styles,
          padding: variant === "text" ? "12px 24px" : "16px 32px",
          fontSize: `${fontSize * 1.15}px`,
        };
        break;
    }

    // Apply hover effects if hovering
    if (isHovering && hoverEffect && !disabled) {
      styles = {
        ...styles,
        backgroundColor:
          hoverColor ||
          (variant === "outlined" || variant === "text"
            ? `rgba(${hexToRgb(backgroundColor)}, 0.1)`
            : darkenColor(backgroundColor, 10)),
        color: hoverTextColor || color,
        transform: "translateY(-2px)",
        boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
      };
    }

    return styles;
  };

  // Helper function to darken a color
  const darkenColor = (hex: string, percent: number) => {
    // Remove the # if it exists
    hex = hex.replace("#", "");

    // Convert to RGB
    let r = parseInt(hex.substring(0, 2), 16);
    let g = parseInt(hex.substring(2, 4), 16);
    let b = parseInt(hex.substring(4, 6), 16);

    // Decrease RGB values by percentage
    r = Math.floor((r * (100 - percent)) / 100);
    g = Math.floor((g * (100 - percent)) / 100);
    b = Math.floor((b * (100 - percent)) / 100);

    // Convert back to hex
    return "#" + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
  };

  // Helper function to convert hex to rgb
  const hexToRgb = (hex: string) => {
    // Remove the # if it exists
    hex = hex.replace("#", "");

    // Convert to RGB
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);

    return `${r}, ${g}, ${b}`;
  };

  return (
    <div
      ref={(ref: HTMLDivElement | null) => {
        if (ref) connect(drag(ref));
      }}
      onClick={(e) => {
        e.stopPropagation();
      }}
      className="button-component"
      style={{
        margin,
        outline: selected ? "2px solid blue" : "none",
        position: "relative",
        width: width === "full" ? "100%" : "auto",
        display: "inline-block",
      }}
    >
      <button
        className={`btn btn-${variant} btn-${size}`}
        style={getButtonStyles()}
        onClick={(e) => {
          e.stopPropagation(); // Prevent event from bubbling up
          if (!disabled) {
            // Execute onClick handler if provided
            if (onClick) onClick();
            // Open URL in new tab if provided
            if (url) window.open(url, "_blank");
          }
        }}
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
        disabled={disabled}
      >
        {icon && iconPosition === "left" && (
          <span
            className="button-icon button-icon-left"
            style={{ marginRight: "8px" }}
          >
            <i className={icon}></i>
          </span>
        )}
        {text}
        {icon && iconPosition === "right" && (
          <span
            className="button-icon button-icon-right"
            style={{ marginLeft: "8px" }}
          >
            <i className={icon}></i>
          </span>
        )}
      </button>
    </div>
  );
};

export const ButtonSettings = () => {
  const {
    actions: { setProp },
    props,
  } = useNode((node) => ({
    props: node.data.props || {},
  }));

  return (
    <div className="settings-container">
      <h4>Button Settings</h4>
      <div className="form-group">
        <label>Button Text</label>
        <input
          type="text"
          className="form-control"
          value={props.text || ""}
          onChange={(e) =>
            setProp((props: ButtonProps) => (props.text = e.target.value))
          }
        />
      </div>

      <div className="form-group">
        <label>Variant</label>
        <select
          className="form-control"
          value={props.variant || "primary"}
          onChange={(e) =>
            setProp(
              (props: ButtonProps) =>
                (props.variant = e.target.value as ButtonVariant)
            )
          }
        >
          <option value="primary">Primary</option>
          <option value="secondary">Secondary</option>
          <option value="outlined">Outlined</option>
          <option value="text">Text</option>
        </select>
      </div>

      <div className="form-group">
        <label>Size</label>
        <select
          className="form-control"
          value={props.size || "medium"}
          onChange={(e) =>
            setProp(
              (props: ButtonProps) =>
                (props.size = e.target.value as ButtonSize)
            )
          }
        >
          <option value="small">Small</option>
          <option value="medium">Medium</option>
          <option value="large">Large</option>
        </select>
      </div>

      <div className="form-group">
        <label>Width</label>
        <select
          className="form-control"
          value={props.width || "auto"}
          onChange={(e) =>
            setProp(
              (props: ButtonProps) =>
                (props.width = e.target.value as ButtonWidth)
            )
          }
        >
          <option value="auto">Auto</option>
          <option value="full">Full Width</option>
        </select>
      </div>

      <div className="form-group">
        <label>URL</label>
        <input
          type="text"
          className="form-control"
          value={props.url || ""}
          onChange={(e) =>
            setProp((props: ButtonProps) => (props.url = e.target.value))
          }
          placeholder="https://example.com"
        />
      </div>

      <div className="form-group">
        <label>Icon</label>
        <input
          type="text"
          className="form-control"
          value={props.icon || ""}
          onChange={(e) =>
            setProp((props: ButtonProps) => (props.icon = e.target.value))
          }
          placeholder="e.g. fa fa-arrow-right"
        />
      </div>

      <div className="form-group">
        <label>Icon Position</label>
        <select
          className="form-control"
          value={props.iconPosition || "left"}
          onChange={(e) =>
            setProp(
              (props: ButtonProps) =>
                (props.iconPosition = e.target.value as "left" | "right")
            )
          }
        >
          <option value="left">Left</option>
          <option value="right">Right</option>
        </select>
      </div>

      <div className="form-group">
        <label>Background Color</label>
        <input
          type="color"
          className="form-control color-picker"
          value={props.backgroundColor || "#007bff"}
          onChange={(e) =>
            setProp(
              (props: ButtonProps) => (props.backgroundColor = e.target.value)
            )
          }
        />
      </div>

      <div className="form-group">
        <label>Text Color</label>
        <input
          type="color"
          className="form-control color-picker"
          value={props.color || "#ffffff"}
          onChange={(e) =>
            setProp((props: ButtonProps) => (props.color = e.target.value))
          }
        />
      </div>

      <div className="form-group">
        <label>Border Color (for outlined variant)</label>
        <input
          type="color"
          className="form-control color-picker"
          value={props.borderColor || props.backgroundColor || "#007bff"}
          onChange={(e) =>
            setProp(
              (props: ButtonProps) => (props.borderColor = e.target.value)
            )
          }
        />
      </div>

      <div className="form-group">
        <label>Font Size (px)</label>
        <input
          type="number"
          className="form-control"
          value={props.fontSize || 16}
          onChange={(e) =>
            setProp(
              (props: ButtonProps) =>
                (props.fontSize = parseInt(e.target.value))
            )
          }
        />
      </div>

      <div className="form-group">
        <label>Font Weight</label>
        <select
          className="form-control"
          value={props.fontWeight || "normal"}
          onChange={(e) =>
            setProp((props: ButtonProps) => (props.fontWeight = e.target.value))
          }
        >
          <option value="normal">Normal</option>
          <option value="bold">Bold</option>
          <option value="lighter">Lighter</option>
        </select>
      </div>

      <div className="form-group">
        <label>Border Radius (px)</label>
        <input
          type="number"
          className="form-control"
          value={props.borderRadius || 4}
          onChange={(e) =>
            setProp(
              (props: ButtonProps) =>
                (props.borderRadius = parseInt(e.target.value))
            )
          }
        />
      </div>

      <div className="form-group">
        <label>Padding</label>
        <input
          type="text"
          className="form-control"
          value={props.padding || ""}
          onChange={(e) =>
            setProp((props: ButtonProps) => (props.padding = e.target.value))
          }
          placeholder="e.g. 10px 20px"
        />
      </div>

      <div className="form-group">
        <label>Margin</label>
        <input
          type="text"
          className="form-control"
          value={props.margin || ""}
          onChange={(e) =>
            setProp((props: ButtonProps) => (props.margin = e.target.value))
          }
          placeholder="e.g. 10px 0"
        />
      </div>

      <div className="form-group">
        <label>Hover Effect</label>
        <div>
          <input
            type="checkbox"
            checked={props.hoverEffect !== false}
            onChange={(e) =>
              setProp(
                (props: ButtonProps) => (props.hoverEffect = e.target.checked)
              )
            }
          />
          <span style={{ marginLeft: "8px" }}>Enable hover effects</span>
        </div>
      </div>

      {props.hoverEffect !== false && (
        <>
          <div className="form-group">
            <label>Hover Background Color</label>
            <input
              type="color"
              className="form-control color-picker"
              value={props.hoverColor || props.backgroundColor || "#0069d9"}
              onChange={(e) =>
                setProp(
                  (props: ButtonProps) => (props.hoverColor = e.target.value)
                )
              }
            />
          </div>

          <div className="form-group">
            <label>Hover Text Color</label>
            <input
              type="color"
              className="form-control color-picker"
              value={props.hoverTextColor || props.color || "#ffffff"}
              onChange={(e) =>
                setProp(
                  (props: ButtonProps) =>
                    (props.hoverTextColor = e.target.value)
                )
              }
            />
          </div>
        </>
      )}

      <div className="form-group">
        <label>Disabled</label>
        <div>
          <input
            type="checkbox"
            checked={props.disabled === true}
            onChange={(e) =>
              setProp(
                (props: ButtonProps) => (props.disabled = e.target.checked)
              )
            }
          />
          <span style={{ marginLeft: "8px" }}>Disable button</span>
        </div>
      </div>
    </div>
  );
};

Button.craft = {
  props: {
    text: "Click Me",
    backgroundColor: "#007bff",
    color: "#ffffff",
    fontSize: 16,
    fontWeight: "normal", // Add fontWeight to craft props
    borderRadius: 4,
    padding: "12px 24px",
    margin: "0",
    url: "",
    variant: "primary",
    size: "medium",
    width: "auto",
    icon: "",
    iconPosition: "left",
    hoverEffect: true,
    hoverColor: "",
    hoverTextColor: "",
    disabled: false,
    borderColor: "",
    onClick: undefined,
  },
  related: {
    settings: ButtonSettings,
  },
};
